import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import MetricCards from '../../GscTools/components/MetricCards';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { comparability, metricsFor } from '../../GscTools/metrics';

/**
 * Block 03 — the active group's daily series, its baseline aligned by day behind it.
 *
 * Both windows are fetched separately with the identical rule, device, country and source, so a
 * baseline that fails leaves the current line drawn and says so beside it. Days Search Console did
 * not return stay gaps — the shared chart is handed nulls, never zeroes and never an interpolation.
 *
 * The cards are summed from the days that came back, so the cards and the line under them are the
 * same figures. They will not always match the group's row in block 02 exactly, and that is the
 * honest outcome: an aggregate for a window is not the sum of the days Search Console chose to
 * return within it. Picking a card changes which metric the line draws; nothing is refetched.
 */
function GroupTrend({
    t,
    scope,
    group,
    status,
    error,
    baselineError,
    current,
    baseline,
    metric,
    onMetric,
}) {
    const comparison = comparability(current?.coverage, baseline?.coverage ?? null);

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">
                        03 ·{' '}
                        {group
                            ? t('tools.content_groups.trend.title', { group })
                            : t('tools.content_groups.trend.title_idle')}
                    </p>
                    <p className="gsc-tool-sub">{t('tools.content_groups.trend.description')}</p>
                </div>
            </div>

            <div className="card-body">
                {!group && (
                    <div className="gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-area-chart" />
                        </span>
                        <h4>{t('tools.content_groups.trend.idle_title')}</h4>
                        <p>{t('tools.content_groups.trend.idle')}</p>
                    </div>
                )}

                {!!group && status === 'loading' && <Loading padding={20} />}

                {!!group && status === 'error' && (
                    <div className="gsc-tool-banner error">
                        {error ?? t('global.something_went_wrong')}
                    </div>
                )}

                {!!group && status === 'done' && (
                    <>
                        {!!baselineError && (
                            <div className="gsc-tool-banner error">
                                {t('tools.content_groups.trend.baseline_failed', {
                                    reason: baselineError,
                                })}
                            </div>
                        )}

                        <MetricCards
                            metrics={metricsFor(scope.searchType)}
                            totals={current?.totals}
                            priorTotals={baseline?.totals}
                            comparison={comparison}
                            selectedMetric={metric}
                            onSelectMetric={onMetric}
                        />

                        <p className="gsc-tool-hint">{t('tools.content_groups.trend.pick_card')}</p>

                        <PeriodTrend
                            metric={metric}
                            current={current}
                            baseline={baseline}
                            fileName={t('tools.content_groups.trend.title', { group })}
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.content_groups.trend.sum_note')}
                        </p>
                        <p className="gsc-tool-table-note">
                            {t('tools.content_groups.trend.gaps_note')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(GroupTrend);
